import React from 'react'
import Dinero from 'dinero.js'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Error as ErrorIcon, InfoOutlined as InfoIcon } from '@mui/icons-material_modern'
import { Stack } from '@mui/material_modern'
import { calcAdjustments, formatNumber, setDecimalPrecision, formatFeeOrChargeValue } from 'utils/helpers'
import { calculateTotalValue, generateAdjustmentTextFromMaterial } from 'utils/data-manipulation/common'
import { calculateFeePromotionValue } from 'utils/fee-promotion-calculator'
import { DEFAULT_SUPPLIER_TYPES, MEASURE_UNIT, ROUNDING_PRECISIONS, IBT_ROW_TYPES } from 'utils/constants'
import { roundTo, makeCommaSeparatedString } from 'utils/data-manipulation/valueFormatter'
import { isDifferentialOversteppedScaleView } from 'components/containers/inbound-tickets/utils'
import PaymentHoldTooltip from 'components/containers/inbound-tickets/components/PaymentHoldTooltip'
import { IconButton } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'

const priceToDisplay = ({ rowData, pricePrepend, defaultYardUm }) =>
  rowData?.weightAndPrice?.netPrice
    ? `${pricePrepend}${setDecimalPrecision(
        Dinero({
          ...(rowData?.weightAndPrice?.customerPreferredPrice ||
            rowData?.weightAndPrice?.netPrice || { amount: 0, precision: 4, currency: 'USD' }),
        }).toUnit(),
      )}/${(
        rowData?.weightAndPrice?.customerPreferredUm ||
        rowData?.weightAndPrice?.um ||
        defaultYardUm
      ).toUpperCase()}`
    : '---'

const getAdditionalText = ({ rowData, type, pricePrepend, defaultYardUm }) => {
  let text = null
  if (rowData?.rowType === IBT_ROW_TYPES.SCRAP_VEHICLE) {
    text = rowData?.vin
  } else if (type === 'PRINT_PURCHASE_TICKET' || type === 'SCALE_VIEW_TICKET') {
    text = priceToDisplay({ rowData, pricePrepend, defaultYardUm })
  }
  return text
}

export const getPrintRowValue = ({ rowData, type, pricePrepend, defaultYardUm }) => {
  return `${rowData.rowIndex}. ${rowData?.materialName || rowData?.materialType?.commonName}${rowData?.overrideName ? ` - ${rowData.overrideName}` : ''} ${
    type !== 'PRINT_SCALE_TICKET' || rowData?.rowType === IBT_ROW_TYPES.SCRAP_VEHICLE
      ? `(${getAdditionalText({ rowData, type, pricePrepend, defaultYardUm })})`
      : ''
  }`
}

// Reusable delete button component for table rows
const DeleteActionBtn = ({ onDeleteClick, id }) => (
  <IconButton
    color="error"
    size="small"
    sx={{
      fill: 'red',
      width: '15px',
      height: '15px',
    }}
    aria-label="delete"
    onClick={event => {
      event.stopPropagation()
      onDeleteClick(id)
    }}
  >
    <DeleteIcon fontSize="small" />
  </IconButton>
)

export const useCreateMaterialsTableRow = ({
  type,
  materials,
  truckWeight,
  subTotalOverride,
  taxRate,
  isPrint,
  showWeightDecimals,
  thresholdAmount,
  thresholdUM,
  canFlagManualScaleReadOverride,
  chargeback,
  pricePrepend,
  enableMaterialPaymentHolds,
  supplier,
  effectiveDateIBT,
  enablePullApartFeatures,
  isTransfer,
  onRowDeleteClick,
  defaultYardUm,
}) => {
  const calculateMaterialTotal = material => {
    const gross = Number(parseFloat(material?.weightAndPrice?.gross?.amount).toFixed(showWeightDecimals ? 1 : 0))
    const tare = Number(parseFloat(material?.weightAndPrice?.tare?.amount || 0).toFixed(showWeightDecimals ? 1 : 0))
    return roundTo(gross - tare - calcAdjustments(material).weightAdjustments, ROUNDING_PRECISIONS.WEIGHT)
  }
  const validMaterials = materials.filter(material => material.materialTypeId !== undefined)
  const totalGross = validMaterials.reduce((aggregate, material) => {
    const gross = Number(parseFloat(material?.weightAndPrice?.gross?.amount).toFixed(showWeightDecimals ? 1 : 0))
    return roundTo(aggregate + gross, ROUNDING_PRECISIONS.WEIGHT)
  }, 0)
  const totalTare = validMaterials.reduce((aggregate, material) => {
    const tare = Number(parseFloat(material?.weightAndPrice?.tare?.amount || 0).toFixed(showWeightDecimals ? 1 : 0))
    return roundTo(aggregate + tare, ROUNDING_PRECISIONS.WEIGHT)
  }, 0)
  const totalNet = validMaterials.reduce(
    (aggregate, material) => roundTo(aggregate + calculateMaterialTotal(material), ROUNDING_PRECISIONS.WEIGHT),
    0,
  )
  const materialSubTotal = validMaterials.reduce((aggregate, material) => {
    const value = Dinero({ ...material?.weightAndPrice?.netValue }).toUnit()
    return roundTo(aggregate + value, ROUNDING_PRECISIONS.COST)
  }, 0)
  const totalTax = setDecimalPrecision(
    roundTo((subTotalOverride || subTotalOverride === 0 ? subTotalOverride : materialSubTotal) * parseFloat(taxRate)) /
      100,
    { decimalPrecisionOverride: 2, shouldFormatNumber: false },
  )
  const totalDeductions = validMaterials.reduce((aggregate, material) => {
    const { weightAdjustments } = calcAdjustments(material)
    return roundTo(aggregate + weightAdjustments, ROUNDING_PRECISIONS.WEIGHT)
  }, 0)
  const truckWeightNet = Number(truckWeight?.gross?.amount) - Number(truckWeight?.tare?.amount)
  const totalContractQuantityObj = validMaterials.reduce((aggregate, material) => {
    const quantity = material.targetWeight
    return {
      quantity: Number(aggregate.quantity || 0) + Number(quantity),
      um: !aggregate.um || material.um === aggregate.um ? material.um : 'Mixed',
    }
  }, {})
  const differential = truckWeightNet - totalNet > 0 ? truckWeightNet - totalNet : ''
  const isDifferentialExceeded = isDifferentialOversteppedScaleView(
    differential,
    totalNet,
    thresholdAmount,
    thresholdUM,
  )
  const totalContractQuantity =
    totalContractQuantityObj.um === 'Mixed' ? totalContractQuantityObj.um : totalContractQuantityObj.quantity

  const generateTotalRowName = () => {
    if (taxRate > 0) return 'Total After Tax'
    if (type === 'SCALE_VIEW_TICKET') return 'Load Total'
    return 'Total'
  }

  const generateFeeOrPromotionRowValue = rowData => {
    // Use the same fee calculation logic as the rest of the application
    const calculatedValue = calculateFeePromotionValue(rowData, materialSubTotal, chargeback || 0, true)

    const isNegativeType = rowData.rowType === IBT_ROW_TYPES.FEE
    return formatFeeOrChargeValue(calculatedValue, pricePrepend, isNegativeType, 2)
  }

  return (rowData = {}, errors = {}) => [
    {
      headerRowValue: '#',
      qualifier: ['SCALE_VIEW_TICKET', 'PURCHASE_ORDER', 'SALES_ORDER'].indexOf(type) > -1,
      materialRowValue: <Typography variant="body2">{rowData?.rowIndex}</Typography>,
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'center',
        key: `${rowData?.id}-rowNumber`,
      },
    },
    {
      headerRowValue: 'Material',
      qualifier: ['SCALE_VIEW_TICKET'].indexOf(type) > -1,
      materialRowValue: (
        <Stack direction="column" spacing={1}>
          <Typography variant="body2">{`${rowData?.materialName || rowData?.materialType?.commonName}${rowData?.overrideName ? ` - ${rowData.overrideName}` : ''}`}</Typography>
          {rowData?.rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ? (
            <Typography variant="body3">{getAdditionalText({ rowData, type, pricePrepend, defaultYardUm })}</Typography>
          ) : null}
          {!isPrint &&
          enableMaterialPaymentHolds &&
          supplier &&
          supplier?.supplierType === DEFAULT_SUPPLIER_TYPES.RETAIL &&
          effectiveDateIBT ? (
            <PaymentHoldTooltip
              rulesArray={rowData?.materialType?.rules}
              effectiveDateValue={new Date(effectiveDateIBT)?.toISOString()}
              listView
            />
          ) : null}
        </Stack>
      ),
      subTotalRowValue: 'Subtotal',
      taxRowValue: `Tax (${taxRate}%)`,
      totalRowValue: generateTotalRowName(),
      truckRowValue: 'Truck Total',
      differentialRowValue: (
        <Stack direction="row" alignItems="center">
          Differential
          {isDifferentialExceeded && <ErrorIcon sx={{ fontSize: '14px', ml: 1 }} color="error" />}
        </Stack>
      ),
      chargebackRowValue: 'Freight Charge',
      feeOrPromotionRowValue: (
        <Stack direction="row" alignItems="center">
          {rowData?.name}
          {rowData.showPaymentMethodSpecificIcon && (
            <Tooltip title={`Only applies to ${makeCommaSeparatedString(rowData.eligiblePaymentTypes)} payments`}>
              <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary', ml: 1 }} />
            </Tooltip>
          )}
        </Stack>
      ),
      cellProps: {
        scope: 'row',
        key: `${rowData?.id}-material`,
      },
    },
    {
      headerRowValue: 'Material',
      qualifier: ['PURCHASE_ORDER', 'SALES_ORDER'].indexOf(type) > -1,
      materialRowValue: (
        <Stack direction="column" spacing={1}>
          <Typography variant="body2">{`${rowData?.overrideName || rowData?.materialName || rowData?.materialType?.commonName}`}</Typography>
        </Stack>
      ),
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: generateTotalRowName(),
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        key: `${rowData?.id}-material`,
      },
    },
    {
      headerRowValue: isPrint ? 'G' : 'Gross',
      qualifier: ['PRINT_PURCHASE_TICKET', 'SCALE_VIEW_TICKET', 'PRINT_SCALE_TICKET'].indexOf(type) > -1,
      materialRowValue:
        rowData?.weightAndPrice?.gross?.amount >= 0
          ? `${
              canFlagManualScaleReadOverride && type === 'SCALE_VIEW_TICKET' && rowData?.weightAndPrice?.isManualGross
                ? '*'
                : ''
            }
          ${setDecimalPrecision(rowData?.weightAndPrice?.gross?.amount, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}`
          : '---',
      subTotalRowValue: setDecimalPrecision(totalGross, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      }),
      taxRowValue: '',
      totalRowValue: setDecimalPrecision(totalGross, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      }),
      truckRowValue: truckWeight?.gross
        ? `${canFlagManualScaleReadOverride && type === 'SCALE_VIEW_TICKET' && truckWeight?.isManualGross ? '*' : ''}
          ${setDecimalPrecision(truckWeight?.gross?.amount || 0, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}`
        : '0',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-gross`,
      },
    },
    {
      headerRowValue: isPrint ? 'T' : 'Tare',
      qualifier: ['PRINT_PURCHASE_TICKET', 'SCALE_VIEW_TICKET', 'PRINT_SCALE_TICKET'].indexOf(type) > -1,
      materialRowValue:
        rowData?.weightAndPrice?.tare?.amount >= 0
          ? `${
              canFlagManualScaleReadOverride && type === 'SCALE_VIEW_TICKET' && rowData?.weightAndPrice?.isManualTare
                ? '*'
                : ''
            }
          ${setDecimalPrecision(rowData?.weightAndPrice?.tare?.amount, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}`
          : '---',
      subTotalRowValue: setDecimalPrecision(totalTare, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      }),
      taxRowValue: '',
      totalRowValue: setDecimalPrecision(totalTare, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      }),
      truckRowValue: truckWeight?.tare
        ? `${canFlagManualScaleReadOverride && type === 'SCALE_VIEW_TICKET' && truckWeight?.isManualTare ? '*' : ''}
          ${setDecimalPrecision(truckWeight?.tare?.amount || 0, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}`
        : '0',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-tare`,
        sx: {
          backgroundColor:
            !!errors?.materialGrid &&
            rowData?.materialType?.tareWeightRequired &&
            rowData?.weightAndPrice?.tare === null
              ? 'error.background'
              : '',
          color:
            !!errors?.materialGrid &&
            rowData?.materialType?.tareWeightRequired &&
            rowData?.weightAndPrice?.tare === null
              ? 'error.main'
              : '',
        },
      },
    },
    {
      headerRowValue: 'Adj',
      qualifier: ['PRINT_PURCHASE_TICKET', 'SCALE_VIEW_TICKET', 'PRINT_SCALE_TICKET'].indexOf(type) > -1,
      materialRowValue: generateAdjustmentTextFromMaterial(rowData, pricePrepend, defaultYardUm),
      subTotalRowValue: totalDeductions === 0 ? '---' : formatNumber(totalDeductions),
      taxRowValue: '',
      totalRowValue: totalDeductions === 0 ? '---' : formatNumber(totalDeductions),
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-adj`,
      },
    },
    {
      headerRowValue: 'Net',
      qualifier: ['PRINT_PURCHASE_TICKET', 'SCALE_VIEW_TICKET', 'PRINT_SCALE_TICKET'].indexOf(type) > -1,
      materialRowValue: rowData?.weightAndPrice?.gross
        ? setDecimalPrecision(calculateMaterialTotal(rowData), {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })
        : '---',
      subTotalRowValue: setDecimalPrecision(totalNet, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      }),
      taxRowValue: '',
      totalRowValue: setDecimalPrecision(totalNet, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      }),
      truckRowValue: truckWeightNet
        ? setDecimalPrecision(truckWeightNet, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })
        : '0',
      differentialRowValue: differential
        ? setDecimalPrecision(differential, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })
        : '0',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-net`,
      },
    },
    {
      headerRowValue: 'Description',
      qualifier: ['PURCHASE_ORDER', 'SALES_ORDER'].indexOf(type) > -1,
      materialRowValue: rowData?.description,
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        key: `${rowData?.id}-description`,
      },
    },
    {
      headerRowValue: 'Quantity',
      qualifier: ['PURCHASE_ORDER', 'SALES_ORDER'].indexOf(type) > -1,
      materialRowValue: formatNumber(rowData?.targetWeight),
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: formatNumber(totalContractQuantity),
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-quantity`,
      },
    },
    {
      headerRowValue: 'Price/UM',
      qualifier: ['PURCHASE_ORDER', 'SALES_ORDER'].indexOf(type) > -1,
      materialRowValue: `${pricePrepend}${setDecimalPrecision(rowData?.price)}/${rowData?.um}`,
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-price`,
      },
    },
    {
      headerRowValue: 'Value',
      qualifier: ['PURCHASE_ORDER', 'SALES_ORDER'].indexOf(type) > -1,
      materialRowValue: `${pricePrepend}${setDecimalPrecision((rowData?.targetWeight || 0) * (rowData?.price || 0), {
        decimalPrecisionOverride: 2,
      })}`,
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue:
        materials.length > 0
          ? `${pricePrepend}${setDecimalPrecision(calculateTotalValue(materials), { decimalPrecisionOverride: 2 })}`
          : '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-value`,
      },
    },
    {
      headerRowValue: 'Price/UM',
      qualifier: ['PRINT_PURCHASE_TICKET', 'SCALE_VIEW_TICKET'].indexOf(type) > -1 && !isPrint,
      materialRowValue: priceToDisplay({ rowData, pricePrepend, defaultYardUm }),
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-price-pt`,
      },
    },
    {
      headerRowValue: 'Value',
      qualifier: ['PRINT_PURCHASE_TICKET', 'SCALE_VIEW_TICKET'].indexOf(type) > -1,
      materialRowValue: rowData?.weightAndPrice?.netValue
        ? `${pricePrepend}${setDecimalPrecision(
            Dinero({
              ...rowData?.weightAndPrice?.netValue,
            }).toUnit(),
            { decimalPrecisionOverride: 2 },
          )}`
        : '---',
      subTotalRowValue: `${pricePrepend}${setDecimalPrecision(
        subTotalOverride || subTotalOverride === 0 ? subTotalOverride : materialSubTotal,
        { decimalPrecisionOverride: 2 },
      )}`,
      taxRowValue: `${pricePrepend}${setDecimalPrecision(totalTax, { decimalPrecisionOverride: 2 })}`,
      totalRowValue: `${pricePrepend}${setDecimalPrecision(
        (subTotalOverride || subTotalOverride === 0 ? subTotalOverride : materialSubTotal) + parseFloat(totalTax),
        { decimalPrecisionOverride: 2 },
      )}`,
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: `(${pricePrepend}${setDecimalPrecision(chargeback, { decimalPrecisionOverride: 2 })})`,
      feeOrPromotionRowValue: generateFeeOrPromotionRowValue(rowData),
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-value-pt`,
      },
    },
    {
      headerRowValue: 'FG Tag #',
      qualifier: isTransfer && ['SCALE_VIEW_TICKET'].indexOf(type) > -1,
      materialRowValue: rowData?.finishedGoodTagNumber,
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-fg-tag-number`,
      },
    },
    {
      headerRowValue: 'FG Count',
      qualifier: enablePullApartFeatures && isTransfer && ['SCALE_VIEW_TICKET'].indexOf(type) > -1,
      materialRowValue: rowData?.finishedGoodItemCount,
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: '',
      cellProps: {
        align: 'right',
        key: `${rowData?.id}-fg-item-count`,
      },
    },
    {
      headerRowValue: '',
      qualifier: ['SCALE_VIEW_TICKET'].indexOf(type) > -1,
      materialRowValue: '',
      subTotalRowValue: '',
      taxRowValue: '',
      totalRowValue: '',
      truckRowValue: '',
      differentialRowValue: '',
      chargebackRowValue: '',
      feeOrPromotionRowValue: rowData.canEdit && <DeleteActionBtn onDeleteClick={onRowDeleteClick} id={rowData.id} />,
      cellProps: {
        align: 'center',
        key: `${rowData?.id}-scale-view-delete`,
        sx: { maxWidth: 20 },
      },
    },
  ]
}
